import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Award,
  CheckCircle2,
  Home as HomeIcon,
  MapPin,
  Settings2,
  Shield,
  ShieldCheck,
  Sparkles,
  SunMedium,
  UserCheck,
  Zap,
} from "lucide-react";

import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import Section from "../components/site/Section";
import { Container } from "../components/site/Container";
import { QButton } from "../components/site/QButton";
import { QCard } from "../components/site/QCard";
import { Reveal } from "../components/site/Reveal";
import { QuoteForm } from "../components/site/QuoteForm";
import { TeamBand } from "../components/site/TeamBand";
import { BrandsMarquee } from "../components/site/BrandsMarquee";
import { WarrantyBand } from "../components/site/WarrantyBand";
import { CTABand } from "../components/site/CTABand";
import { SkipToContent } from "../components/site/SkipToContent";
import { SEO } from "../components/site/SEO";
import { TrustStrip } from "../components/site/TrustStrip";
import { IconBadge } from "../components/site/IconBadge";
import { VideoFacade } from "../components/site/VideoFacade";

import { PAGE_META } from "../lib/seo";

import {
  HERO_HEADLINE,
  HERO_SUBHEAD,
  HERO_EYEBROW,
  VALUE_PROPS,
  SERVICES,
  BLOG_TEASERS,
  CONTACT,
  PROMO_VIDEO_POSTER,
  PROMO_VIDEO_POSTER_SRCSET,
  cardSrcSet,
} from "../lib/content";

const ICONS = {
  UserCheck,
  Settings2,
  Home: HomeIcon,
  ShieldCheck,
  SunMedium,
  Zap,
  Award,
};

/* ----------------------------- HERO ----------------------------- */
const Hero = () => (
  <section
    data-testid="hero"
    className="relative bg-white pt-10 md:pt-14 pb-16 md:pb-24 overflow-hidden"
  >
    <Container>
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        {/* LEFT — copy + trust strip + rooftop image */}
        <div className="lg:col-span-7 lg:pt-4">
          <Reveal>
            <div className="qv-eyebrow mb-5 text-brand-blue">
              {HERO_EYEBROW}
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1
              className="qv-h1"
              data-testid="hero-headline"
              style={{ fontSize: "clamp(2.25rem, 4.6vw, 4rem)" }}
            >
              {HERO_HEADLINE}
            </h1>
          </Reveal>

          {/* Streamable promo video — between H1 and lead intro. 16:9 frame.
              Rendered as a click-to-play poster; the player iframe only loads
              after the visitor presses play (see VideoFacade). */}
          <Reveal delay={0.1}>
            <VideoFacade
              embedUrl="https://streamable.com/e/88k363"
              poster={PROMO_VIDEO_POSTER}
              posterSrcSet={PROMO_VIDEO_POSTER_SRCSET}
              // Portrait poster letterboxed in a 16:9 frame: its rendered
              // width is 3/4 of the frame height (= 9/16 of the frame width).
              posterSizes="(min-width: 1024px) 24vw, 42vw"
              posterWidth={600}
              posterHeight={800}
              title="QVolt Solar — promo video"
              className="my-6"
            />
          </Reveal>

          <Reveal delay={0.12}>
            <p
              className="qv-lead mt-6 max-w-[58ch]"
              data-testid="hero-subhead"
            >
              {HERO_SUBHEAD}
            </p>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <QButton
                variant="primary"
                size="lg"
                as="a"
                href="#quote-form"
                iconRight={ArrowRight}
                data-testid="hero-cta-primary"
              >
                Get a free quote
              </QButton>
            </div>
          </Reveal>

          {/* Trust strip — 2 pills (NETCC + Google reviews) */}
          <Reveal delay={0.22}>
            <TrustStrip className="mt-8" testId="hero-trust-strip" />
          </Reveal>
        </div>

        {/* RIGHT — lead capture form */}
        <div className="lg:col-span-5" id="quote">
          <Reveal delay={0.1}>
            <QuoteForm />
          </Reveal>
        </div>
      </div>
    </Container>
  </section>
);

/* --------------------------- WHY ------------------------------- */
const WhyQVolt = () => (
  <Section
    id="why"
    eyebrow="5 reasons to choose my company"
    title="Tailored solar from a real expert. No sales script."
    intro="A small family-owned crew that gives genuine advice and installs systems built around your home, not around a sales target."
    bg="muted"
    testId="section-why"
  >
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
      {VALUE_PROPS.map((vp, i) => {
        const Icon = ICONS[vp.icon] || UserCheck;
        return (
          <Reveal key={vp.title} delay={i * 0.05}>
            <QCard className="h-full" data-testid={`why-card-${i}`}>
              <IconBadge icon={Icon} className="mb-6" />
              <div className="qv-h3 mb-3">{vp.title}</div>
              <p className="qv-body">{vp.body}</p>
            </QCard>
          </Reveal>
        );
      })}
    </div>
  </Section>
);

/* --------------------------- SERVICES --------------------------- */
const Services = () => (
  <Section
    id="services"
    eyebrow="Our solar services"
    title="One team. Every part of your solar journey."
    bg="white"
    testId="section-services"
  >
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
      {SERVICES.map((s, i) => (
        <Reveal key={s.title} delay={i * 0.07}>
          <QCard
            className="h-full p-0 overflow-hidden flex flex-col"
            data-testid={`service-card-${i}`}
          >
            <div className="aspect-[4/3] bg-muted overflow-hidden">
              <img
                src={s.image}
                srcSet={cardSrcSet(s.image)}
                sizes="(min-width: 768px) 30vw, 92vw"
                alt={s.title}
                width={800}
                height={600}
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                data-testid={`service-image-${i}`}
              />
            </div>
            <div className="p-7 md:p-8 flex flex-col flex-1">
              <div className="qv-h3 mb-2">{s.title}</div>
              <p className="qv-body mb-6 flex-1">{s.desc}</p>
              <Link
                to={s.href || "/services"}
                className="inline-flex items-center gap-1.5 text-[14px] font-medium qv-link"
                data-testid={`service-link-${i}`}
              >
                Learn more
                <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
              </Link>
            </div>
          </QCard>
        </Reveal>
      ))}
    </div>
  </Section>
);

/* --------------------------- WARRANTY --------------------------- */
// Reused from /components/site/WarrantyBand — see import.

/* ------------------------- BLOG TEASER -------------------------- */
const BlogTeaser = () => (
  <Section
    id="blog"
    eyebrow="From the blog"
    title="Solar tips, rebates and the truth about installs."
    bg="muted"
    testId="section-blog"
  >
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
      {BLOG_TEASERS.map((p, i) => (
        <Reveal key={p.slug} delay={i * 0.07}>
          <Link
            to={`/learning-centre/${p.slug}`}
            className="group block bg-white border border-border rounded-xl overflow-hidden shadow-sm transition-[box-shadow,transform] duration-200 hover:shadow-md hover:-translate-y-0.5 qv-focus"
            data-testid={`blog-card-${i}`}
          >
            <div className="aspect-[16/10] bg-muted overflow-hidden">
              <img
                src={p.hero_image_url}
                srcSet={cardSrcSet(p.hero_image_url)}
                sizes="(min-width: 768px) 30vw, 92vw"
                alt={p.title}
                width={800}
                height={500}
                loading="lazy"
                decoding="async"
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                data-testid={`blog-image-${i}`}
              />
            </div>
            <div className="p-7">
              <div className="qv-eyebrow text-subtle mb-3">Solar guide</div>
              <h3 className="qv-h3 group-hover:text-brand-blue transition-colors">
                {p.title}
              </h3>
              <div className="mt-5 inline-flex items-center gap-1.5 text-[14px] font-medium text-brand-blue group-hover:text-brand-blue-dark transition-colors">
                Read more
                <ArrowUpRight className="h-4 w-4" strokeWidth={1.75} />
              </div>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
  </Section>
);

/* --------------------------- CTA BAND --------------------------- */
// Reused from /components/site/CTABand — see import.

/* ------------------------------ PAGE --------------------------- */
export default function Home({ seoOverride }) {
  const seoProps = seoOverride || {
    title: PAGE_META.home.title,
    description: PAGE_META.home.description,
    path: "/",
  };
  return (
    <div className="bg-white" data-testid="home-page">
      <SEO
        title={seoProps.title}
        description={seoProps.description}
        path={seoProps.path}
        noindex={seoProps.noindex}
        noCanonical={seoProps.noCanonical}
      />
      <SkipToContent />

      <Header />
      <main id="main">
        <Hero />
        <BrandsMarquee />
        <WhyQVolt />
        <Services />
        <TeamBand />
        <WarrantyBand />
        <BlogTeaser />
        <CTABand />
      </main>
      <Footer />
    </div>
  );
}
